import sharePdfDrawingsCopy from '@/pages/share-pdf-drawings-for-review.content.json';
const SITE_ORIGIN = 'https://pdfmarkuptool.com';

/**
 * Single source of truth for all prerendered marketing pages.
 * Adding a page = one entry here + one eager import + one Route in App.tsx.
 * Everything else (PRERENDER_ROUTES, sitemap, canonicals, footer links) derives
 * from this array automatically.
 *
 * `reviewed` is the page's content date (YYYY-MM-DD): the visible "Page last reviewed"
 * dateline and the sitemap <lastmod> both read it. Move it only when the page's copy is
 * reviewed or changed -- never set it from the build time.
 * Limits (seoHygiene.test.tsx): title <= 60 chars, description <= 160 chars.
 */
export const PAGE_REGISTRY = [
    {
        route: '/' as const,
        canonical: `${SITE_ORIGIN}/`,
        label: 'Home',
        changefreq: 'weekly' as const,
        priority: '1.0',
        reviewed: '2026-11-01',
        head: {
            title: 'Free PDF Markup Software -- No Upload, No Account',
            description: 'Free PDF markup software. Annotate and redline PDFs in your browser -- nothing uploaded, no account. A Bluebeam alternative for Mac, Windows, and Linux.',
        },
    },
    {
        route: '/bluebeam-for-mac' as const,
        canonical: `${SITE_ORIGIN}/bluebeam-for-mac/`,
        label: 'Bluebeam for Mac',
        changefreq: 'monthly' as const,
        priority: '0.8',
        reviewed: '2026-11-01',
        head: {
            title: 'No Bluebeam Mac Download -- Try PDF Markup Tool',
            description: 'No Bluebeam Mac download exists. PDF Markup Tool runs in Safari or Chrome on macOS -- no install, no Windows VM, no Parallels. Free.',
        },
    },
    {
        route: '/bluebeam-revu-free-alternative' as const,
        canonical: `${SITE_ORIGIN}/bluebeam-revu-free-alternative/`,
        label: 'Bluebeam Revu Free Alternative',
        changefreq: 'monthly' as const,
        priority: '0.8',
        reviewed: '2026-11-01',
        head: {
            title: 'Bluebeam Revu Free Alternative -- Browser PDF Markup',
            description: 'Need a free way to mark up PDFs during drawing review? PDF Markup Tool runs in your browser with no subscription, no install, no account, and no file upload.',
        },
    },
    {
        route: '/bluebeam-revu-20-end-of-life' as const,
        canonical: `${SITE_ORIGIN}/bluebeam-revu-20-end-of-life/`,
        label: 'Bluebeam Revu 20 End of Life',
        changefreq: 'monthly' as const,
        priority: '0.8',
        reviewed: '2026-11-01',
        head: {
            title: 'Revu 20 End of Life: Dates and PDF Markup Options',
            description: 'Revu 20 end of life is December 31, 2026. See the support timeline and ways to keep marking up drawings in your browser with PDF Markup Tool.',
        },
    },
    {
        route: '/measure-pdf-online' as const,
        canonical: `${SITE_ORIGIN}/measure-pdf-online/`,
        label: 'Measure PDF Online',
        changefreq: 'monthly' as const,
        priority: '0.8',
        reviewed: '2026-10-27',
        head: {
            title: 'Measure PDF Online -- Distance Measurement for PDF Plans',
            description: 'Measure distances on a PDF plan in your browser. Calibrate to drawing scale, draw a dimension and read the real-world length. Free, no upload, no account.',
        },
    },
    {
        route: '/drawing-scales' as const,
        canonical: `${SITE_ORIGIN}/drawing-scales/`,
        label: 'Drawing Scales',
        changefreq: 'monthly' as const,
        priority: '0.7',
        reviewed: '2026-10-27',
        head: {
            title: 'Drawing Scales and How to Check Them on a PDF',
            description: 'Reference common imperial and metric drawing scales, learn why a PDF scale can be wrong, and check calibration before measuring a plan.',
        },
    },
    {
        route: '/measure-area-pdf' as const,
        canonical: `${SITE_ORIGIN}/measure-area-pdf/`,
        label: 'Measure Area on a PDF',
        changefreq: 'monthly' as const,
        priority: '0.8',
        reviewed: '2026-11-01',
        head: {
            title: 'Measure Area on a PDF Floor Plan',
            description: 'Measure PDF floor plan area and perimeter after calibration. Outline irregular rooms with a polygon, use rectangles or ellipses, and export measurements to CSV.',
        },
    },
    {
        route: '/calibrate-pdf-scale' as const,
        canonical: `${SITE_ORIGIN}/calibrate-pdf-scale/`,
        label: 'Calibrate PDF Scale',
        changefreq: 'monthly' as const,
        priority: '0.7',
        reviewed: '2026-11-01',
        head: {
            title: 'Calibrate PDF Drawing Scale Online',
            description: 'Set a PDF drawing scale with two known points or a preset, check it against another dimension, then apply it to all sheets or a selected range.',
        },
    },
    {
        route: '/share-pdf-drawings-for-review' as const,
        canonical: `${SITE_ORIGIN}/${sharePdfDrawingsCopy.slug}/`,
        label: sharePdfDrawingsCopy.label,
        changefreq: 'monthly' as const,
        priority: '0.8',
        reviewed: '2026-11-02',
        head: {
            title: sharePdfDrawingsCopy.title,
            description: sharePdfDrawingsCopy.description,
        },
    },
    {
        route: '/ipad-pdf-markup' as const,
        canonical: `${SITE_ORIGIN}/ipad-pdf-markup/`,
        label: 'PDF Markup on iPad',
        changefreq: 'monthly' as const,
        priority: '0.8',
        reviewed: '2026-11-03',
        head: {
            title: 'iPad PDF Markup with Apple Pencil in Safari',
            description: 'Mark up PDF drawings on iPad in Safari: open a sheet from Files, draw with Apple Pencil, navigate with your fingers, and export your work.',
        },
    },
    {
        route: '/redline-pdf-drawing' as const,
        canonical: `${SITE_ORIGIN}/redline-pdf-drawing/`,
        label: 'Plan Markup and Redlines',
        changefreq: 'monthly' as const,
        priority: '0.8',
        reviewed: '2026-11-04',
        head: {
            title: 'Plan Markup: Redline PDF Drawings and Send Comments',
            description: 'Plan markup and drawing markup in your browser: mark up plans with clouds, callouts, text and calibrated dimensions, then export a PDF and CSV.',
        },
    },
    {
        route: '/free-pdf-markup-tools' as const,
        canonical: `${SITE_ORIGIN}/free-pdf-markup-tools/`,
        label: 'Free PDF Markup Tools Compared',
        changefreq: 'monthly' as const,
        priority: '0.7',
        reviewed: '2026-11-01',
        head: {
            title: 'Free PDF Markup Tools Compared',
            description: 'Compare free PDF markup tools for construction drawing review: account, local processing, free export limits, platforms, and offline support.',
        },
    },
    {
        route: '/whats-new' as const,
        canonical: `${SITE_ORIGIN}/whats-new/`,
        label: "What's New",
        changefreq: 'weekly' as const,
        priority: '0.8',
        reviewed: '2026-10-26',
        head: {
            title: "What's New in PDF Markup Tool",
            description: 'A dated changelog of new features and fixes in PDF Markup Tool -- photo markup, dimension measurement, Apple Pencil improvements, and more.',
        },
    },
    {
        route: '/de' as const,
        canonical: `${SITE_ORIGIN}/de/`,
        label: 'Deutsch',
        changefreq: 'weekly' as const,
        priority: '0.8',
        reviewed: '2026-11-01',
        head: {
            title: 'Kostenlose PDF-Markup-Software -- Kein Upload, Kein Konto',
            description: 'Kostenlose PDF-Markup-Software. PDFs im Browser kommentieren -- kein Upload, kein Konto. Bluebeam-Alternative für Mac, Windows und Linux.',
        },
    },
    {
        route: '/fr' as const,
        canonical: `${SITE_ORIGIN}/fr/`,
        label: 'Français',
        changefreq: 'weekly' as const,
        priority: '0.8',
        reviewed: '2026-11-01',
        head: {
            title: 'Logiciel PDF gratuit -- Aucun upload, aucun compte',
            description: 'Logiciel de balisage PDF gratuit. Annotez vos PDF dans le navigateur, sans upload ni compte. Alternative à Bluebeam pour Mac, Windows et Linux.',
        },
    },
    {
        route: '/de/pdf-messen' as const,
        canonical: `${SITE_ORIGIN}/de/pdf-messen/`,
        label: 'PDF messen',
        changefreq: 'monthly' as const,
        priority: '0.7',
        reviewed: '2026-11-02',
        head: {
            title: 'PDF messen: Maßstab, Fläche und Umfang',
            description: 'PDF messen: Maßstab kalibrieren, Strecken, Flächen und Umfänge auf Grundrissen erfassen und Messwerte als CSV exportieren.',
        },
    },
    {
        route: '/fr/mesurer-pdf' as const,
        canonical: `${SITE_ORIGIN}/fr/mesurer-pdf/`,
        label: 'Mesurer un PDF',
        changefreq: 'monthly' as const,
        priority: '0.7',
        reviewed: '2026-11-02',
        head: {
            title: 'Mesurer un PDF : échelle, surface et périmètre',
            description: 'Calibrez l’échelle d’un PDF, mesurez distances, surfaces et périmètres sur un plan, puis exportez les mesures en CSV.',
        },
    },
] as const;

export type PageRoute = (typeof PAGE_REGISTRY)[number]['route'];

export type PageRegistryEntry = (typeof PAGE_REGISTRY)[number];

/** Look up the registry entry for a route. Throws if the route is not registered. */
export function pageForRoute(route: PageRoute): PageRegistryEntry {
    const entry = PAGE_REGISTRY.find((p) => p.route === route);
    if (!entry) throw new Error(`No page registry entry for route: ${route}`);
    return entry;
}
